import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { StatCardsSkeleton, TableSkeleton } from "@/components/PageSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import {
  apiGetAuditLog, apiGetAuditLogModules, apiGetAuditLogStats, apiExportAuditLog,
  type PlatformAuditLogEntry, type AuditLogStats,
} from "@/lib/api";
import {
  Activity, Shield, Database, Search, Filter, ChevronLeft, ChevronRight,
  Clock, User, RefreshCw, Calendar, ChevronDown, Download,
} from "lucide-react";
import { format, formatDistanceToNow, startOfToday, subDays, startOfMonth } from "date-fns";

// ── Date range presets ─────────────────────────────────────────────────────────
const fmt = (d: Date) => format(d, "yyyy-MM-dd");
const DATE_PRESETS: { id: string; range: () => { from: string; to: string } }[] = [
  { id: "today",     range: () => ({ from: fmt(startOfToday()),          to: fmt(new Date()) }) },
  { id: "last7",     range: () => ({ from: fmt(subDays(new Date(), 6)),  to: fmt(new Date()) }) },
  { id: "last30",    range: () => ({ from: fmt(subDays(new Date(), 29)), to: fmt(new Date()) }) },
  { id: "thisMonth", range: () => ({ from: fmt(startOfMonth(new Date())), to: fmt(new Date()) }) },
  { id: "all",       range: () => ({ from: "", to: "" }) },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const ACTION_CONFIG: Record<string, { color: string; label: string }> = {
  POST:   { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Create" },
  PUT:    { color: "bg-blue-100 text-blue-700 border-blue-200",          label: "Update" },
  PATCH:  { color: "bg-amber-100 text-amber-700 border-amber-200",       label: "Patch" },
  DELETE: { color: "bg-red-100 text-red-700 border-red-200",             label: "Delete" },
  WIDGET: { color: "bg-slate-100 text-slate-500 border-slate-200",       label: "Viewed" },
};

const ALL_ACTIONS = [
  { value: "POST",   label: "Create" },
  { value: "PUT",    label: "Update" },
  { value: "PATCH",  label: "Patch" },
  { value: "DELETE", label: "Delete" },
  { value: "WIDGET", label: "Viewed" },
];

const MODULE_COLORS: Record<string, string> = {
  "Authentication":     "bg-purple-100 text-purple-700",
  "Employees":          "bg-blue-100 text-blue-700",
  "Leave":              "bg-teal-100 text-teal-700",
  "Documents":          "bg-indigo-100 text-indigo-700",
  "Payroll":            "bg-green-100 text-green-700",
  "Salary Config":      "bg-lime-100 text-lime-700",
  "Performance":        "bg-orange-100 text-orange-700",
  "Surveys":            "bg-pink-100 text-pink-700",
  "Workflows":          "bg-violet-100 text-violet-700",
  "Onboarding":         "bg-cyan-100 text-cyan-700",
  "Offboarding":        "bg-rose-100 text-rose-700",
  "Organization":       "bg-sky-100 text-sky-700",
  "Teams":              "bg-emerald-100 text-emerald-700",
  "Raise Concern":      "bg-red-100 text-red-700",
  "Settings":           "bg-gray-100 text-gray-700",
  "Field Permissions":  "bg-yellow-100 text-yellow-700",
  "Nav Permissions":    "bg-yellow-100 text-yellow-700",
  "Corporate Calendar": "bg-blue-100 text-blue-700",
  "Expenses":           "bg-amber-100 text-amber-700",
  "Medical Checks":     "bg-fuchsia-100 text-fuchsia-700",
  "Certifications":     "bg-emerald-100 text-emerald-700",
  "Equipment":          "bg-stone-100 text-stone-700",
  "Recruitment":        "bg-indigo-100 text-indigo-700",
  "Document Templates": "bg-violet-100 text-violet-700",
  "GDPR":               "bg-red-100 text-red-700",
};

function moduleColor(module: string): string {
  return MODULE_COLORS[module] ?? "bg-muted text-muted-foreground";
}

// ── Multiselect dropdown ──────────────────────────────────────────────────────

interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  width?: string;
  renderLabel?: (v: string) => string;
}

function MultiSelect({ options, selected, onChange, placeholder, width = "w-44", renderLabel }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (renderLabel ? renderLabel(selected[0]) : selected[0])
      : `${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 ${width} justify-between font-normal text-sm`}
        >
          <span className="truncate text-left">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-52" align="start">
        {options.length === 0 && (
          <p className="text-xs text-muted-foreground px-2 py-1.5">{t("auditLog.filters.noOptions", "No options")}</p>
        )}
        {options.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-muted/60 select-none"
          >
            <Checkbox
              checked={selected.includes(opt)}
              onCheckedChange={() => toggle(opt)}
            />
            {renderLabel ? renderLabel(opt) : opt}
          </label>
        ))}
        {selected.length > 0 && (
          <>
            <div className="border-t mt-1 pt-1" />
            <button
              onClick={() => onChange([])}
              className="w-full text-left text-xs text-muted-foreground px-2 py-1 hover:bg-muted/60 rounded"
            >
              {t("auditLog.filters.clearSelection", "Clear selection")}
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Stats cards ──────────────────────────────────────────────────────────────

function StatsBar({ stats }: { stats: AuditLogStats }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Card className="shadow-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{t("auditLog.stats.totalActions", "Total actions")}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
            <Calendar className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{stats.today.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{t("auditLog.stats.today", "Today")}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-card lg:col-span-2">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("auditLog.stats.topModules", "Top modules")}</p>
          <div className="flex flex-wrap gap-1.5">
            {stats.byModule.slice(0, 6).map((m) => (
              <span key={m.module} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${moduleColor(m.module)}`}>
                {m.module}
                <span className="font-bold">{m.count}</span>
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

function AuditRow({ entry }: { entry: PlatformAuditLogEntry }) {
  const { t } = useTranslation();
  const ac = ACTION_CONFIG[entry.action] ?? { color: "bg-muted text-muted-foreground border-muted", label: entry.action };
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b last:border-0 hover:bg-muted/30 transition-colors">
      <span className={`shrink-0 mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ac.color}`}>
        {t(`auditLog.actions.${entry.action.toLowerCase()}`, ac.label)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug">{entry.message}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${moduleColor(entry.module)}`}>
            {entry.module}
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            {entry.userName}
            {entry.userEmail && <span className="text-muted-foreground/60">({entry.userEmail})</span>}
          </span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
        </p>
        <p className="text-[10px] text-muted-foreground/60 whitespace-nowrap mt-0.5">
          {format(new Date(entry.createdAt), "dd MMM yyyy HH:mm:ss")}
        </p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AuditLog() {
  const { t } = useTranslation();
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterModules, setFilterModules] = useState<string[]>([]);
  const [filterActions, setFilterActions] = useState<string[]>([]);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo,   setFilterTo]   = useState("");
  const [activePreset, setActivePreset] = useState<string | null>("all");
  const [exporting, setExporting]   = useState(false);
  const LIMIT = 50;

  const applyPreset = (id: string) => {
    const preset = DATE_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    const { from, to } = preset.range();
    setFilterFrom(from);
    setFilterTo(to);
    setActivePreset(id);
    setPage(1);
  };

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["audit-log", page, search, filterModules, filterActions, filterFrom, filterTo],
    queryFn: () => apiGetAuditLog({
      page,
      limit:   LIMIT,
      search:  search || undefined,
      modules: filterModules.length ? filterModules : undefined,
      actions: filterActions.length ? filterActions : undefined,
      from:    filterFrom || undefined,
      to:      filterTo   || undefined,
    }),
    staleTime: 30_000,
  });

  const { data: modules = [] } = useQuery({
    queryKey: ["audit-log-modules"],
    queryFn: apiGetAuditLogModules,
    staleTime: 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ["audit-log-stats"],
    queryFn: apiGetAuditLogStats,
    staleTime: 60_000,
  });

  const applySearch = () => { setSearch(searchInput); setPage(1); };

  const resetFilters = () => {
    setSearch(""); setSearchInput("");
    setFilterModules([]); setFilterActions([]);
    setFilterFrom(""); setFilterTo("");
    setActivePreset("all");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiExportAuditLog({
        modules: filterModules.length ? filterModules : undefined,
        actions: filterActions.length ? filterActions : undefined,
        search:  search || undefined,
        from:    filterFrom || undefined,
        to:      filterTo   || undefined,
      });
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = search || filterModules.length || filterActions.length || filterFrom || filterTo;

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-slate-500/10 flex items-center justify-center shrink-0">
              <Database className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("auditLog.title", "Audit Log")}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("auditLog.subtitle", "Every platform action with timestamp, user, module and description.")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-1.5">
              <Download className={`h-3.5 w-3.5 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? t("auditLog.exporting", "Exporting…") : t("auditLog.export", "Export CSV")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5">
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              {t("auditLog.refresh", "Refresh")}
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats ? <StatsBar stats={stats} /> : <StatCardsSkeleton count={4} />}

        {/* Filters */}
        <Card className="shadow-card">
          <CardContent className="p-4 space-y-4">
            {/* Row 1: search + module + action + clear */}
            <div className="flex flex-wrap gap-4 items-end">
              {/* Search */}
              <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                <label className="text-[11px] font-medium text-muted-foreground">{t("auditLog.filters.searchLabel", "Search")}</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applySearch()}
                      placeholder={t("auditLog.filters.search", "Search message or user…")}
                      className="pl-8 h-9"
                    />
                  </div>
                  <Button size="sm" onClick={applySearch} className="h-9">{t("auditLog.filters.searchBtn", "Search")}</Button>
                </div>
              </div>

              {/* Modules multiselect */}
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-muted-foreground">{t("auditLog.filters.module", "Module")}</label>
                <MultiSelect
                  options={modules}
                  selected={filterModules}
                  onChange={(v) => { setFilterModules(v); setPage(1); }}
                  placeholder={t("auditLog.filters.allModules", "All modules")}
                  width="w-44"
                />
              </div>

              {/* Actions multiselect */}
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-muted-foreground">{t("auditLog.filters.action", "Action")}</label>
                <MultiSelect
                  options={ALL_ACTIONS.map((a) => a.value)}
                  selected={filterActions}
                  onChange={(v) => { setFilterActions(v); setPage(1); }}
                  placeholder={t("auditLog.filters.allActions", "All actions")}
                  width="w-40"
                  renderLabel={(v) => t(`auditLog.actions.${v.toLowerCase()}Label`, ALL_ACTIONS.find((a) => a.value === v)?.label ?? v)}
                />
              </div>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 gap-1.5 text-muted-foreground">
                  <Filter className="h-3.5 w-3.5" />{t("auditLog.filters.clear", "Clear")}
                </Button>
              )}
            </div>

            {/* Row 2: date range + presets */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 items-end">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-muted-foreground">{t("auditLog.filters.dateRange", "Date range")}</label>
                <div className="flex items-center gap-2">
                  <DatePicker value={filterFrom} onChange={(v) => { setFilterFrom(v); setActivePreset(null); setPage(1); }} className="h-9 w-44 px-3 text-sm" />
                  <span className="text-muted-foreground text-sm">–</span>
                  <DatePicker value={filterTo} onChange={(v) => { setFilterTo(v); setActivePreset(null); setPage(1); }} className="h-9 w-44 px-3 text-sm" />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-medium text-muted-foreground">{t("auditLog.filters.quickRange", "Quick range")}</label>
                <div className="inline-flex items-center rounded-lg border bg-muted/40 p-0.5 h-9">
                  {DATE_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p.id)}
                      className={`px-2.5 h-8 text-xs font-medium rounded-md transition-colors ${
                        activePreset === p.id
                          ? "bg-background shadow-sm text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t(`auditLog.presets.${p.id}`, p.id)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Log table */}
        <Card className="shadow-card">
          <CardHeader className="pb-0 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {t("auditLog.activity.title", "Activity")}
                {data && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    {t("auditLog.activity.entries", { count: data.total })}
                  </span>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0 mt-3">
            {isLoading ? (
              <div className="p-4">
                <TableSkeleton rows={8} cols={5} />
              </div>
            ) : !data || data.rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {t("auditLog.activity.empty", "No audit log entries found.")}
              </div>
            ) : (
              <>
                {data.rows.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}

                {/* Pagination */}
                {data.pages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-xs text-muted-foreground">
                      {t("auditLog.activity.pageInfo", { page: data.page, pages: data.pages, total: data.total })}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="gap-1"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> {t("auditLog.activity.prev", "Prev")}
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        disabled={page >= data.pages}
                        onClick={() => setPage((p) => p + 1)}
                        className="gap-1"
                      >
                        {t("auditLog.activity.next", "Next")} <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
